/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable import/no-cycle */
import React, { ReactNode, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { IApiQueryOptions } from 'api/ApiClient';
import { FilterButton, ModalFactory, PageActions, PageHeader, PageHeaderAction, PageHeaderContent, PageLabel, Section, SectionHeader, StatusCheckbox, TabNav, TabNavItem, UserTable, UserTableDataProps } from 'components';
import ConnectedSiteSelector from 'components/SiteSelector/ConnectedSiteSelector';
import { SiteSelectorAppearance } from 'components/SiteSelector/types';
import { TableHeaderObject } from 'components/Table/User/UserTable';
import { ConfirmResendInvite } from 'modals';
import ListContext, { FetchDataQueryType, ListContextProvider } from 'providers/ListContextProvider';
import { routeConstants } from 'routes';
import { PermissionGate } from 'screens/Security';
import { TipAcceptanceUsers } from 'screens/TipAcceptance';
import ReviewUserScreen from 'screens/Users/ReviewUser';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { selectCurrentEmployee } from 'store/App';
import { Employees, getActionRequiredEmployeesCountRequest, getEmployeesRequest, selectEmployees, selectReviewRequiredEmployeesTotal } from 'store/Collections';
import { Employee, getEmployeeRequest, resendInvitationRequest } from 'store/Employee';
import { selectCurrentEmployeeOrg, selectCurrentEmployeeSite, selectIsPermitted } from 'store/Employers';
import { selectSiteAcceptance } from 'store/Site';
import { IStore } from 'store/types';
import { AffiliationType, ApiQueryOperator, EmployeeActionRequiredType, EmployeeRole, EmployeesTab, EmployeeStatus, EmployeeType, IApiQueryParam, IEmployee } from 'types';
import UsersFilters from './UsersFilters';

export const DEFAULT_SORT = 'name';

interface IListContentComponentType {
  activeTab: EmployeesTab;
  statusLabels: string[];
  onSortChange: (value: string) => void;
  onStatusChange?: (value: string[]) => void;
  isSortedBy: (value: string) => boolean;
  isSortDesc: (value: string) => boolean;
  pagination?: ReactNode;
  resultsPerPage?: ReactNode;
  limit: number;
  q?: IApiQueryParam[];
}

// To display new Data tableHeader, `value` prop needs to match the prop name of what is coming from the API
const tableHeaders: Record<EmployeesTab, TableHeaderObject[]> = {
  [EmployeesTab.ALL]: [
    {
      label: 'Name',
      value: 'name',
      sortable: true,
    },
    {
      label: 'Site',
      value: 'sites',
      sortable: false,
    },
    {
      label: 'Role',
      value: 'role',
      sortable: false,
    },
    {
      label: 'Job Title',
      value: 'jobTitle',
      sortable: false,
    },
    {
      label: 'Status',
      value: 'status',
      sortable: false,
    },
  ],
  [EmployeesTab.REVIEW]: [
    {
      label: 'Name',
      value: 'name',
      sortable: true,
    },
    {
      label: 'Site',
      value: 'sites',
      sortable: false,
    },
    {
      label: 'Role',
      value: 'role',
      sortable: false,
    },
    {
      label: 'Job Title',
      value: 'jobTitle',
      sortable: false,
    },
    {
      label: 'Invite Method',
      value: 'inviteRecipient',
      align: 'left',
    },
    {
      label: 'Status',
      value: 'status',
      sortable: false,
    },
    {
      label: '',
      value: 'reviewAction',
    },
  ],
  [EmployeesTab.ACCEPTANCE]: [],
};

const pendingStatusFilters = [
  {
    key: 'status',
    operator: ApiQueryOperator.in,
    value: [EmployeeStatus.INVITED, EmployeeStatus.ACTION_REQUIRED],
  },
  {
    key: 'action_required',
    operator: ApiQueryOperator.in,
    value: EmployeeActionRequiredType.EMPLOYER_APPROVAL,
  },
];

const actionRequired = [EmployeeActionRequiredType.EMPLOYER_APPROVAL, EmployeeActionRequiredType.INVITE_EXPIRED, EmployeeActionRequiredType.INVITE_SEND_FAILURE];

const actionRequiredFilters = [
  {
    key: 'status',
    operator: ApiQueryOperator.in,
    value: EmployeeStatus.ACTION_REQUIRED,
  },
  {
    key: 'action_required',
    operator: ApiQueryOperator.in,
    value: actionRequired,
  },
];

const expiredStatusFilters = [
  {
    key: 'status',
    operator: ApiQueryOperator.in,
    value: [EmployeeStatus.INVITED, EmployeeStatus.ACTION_REQUIRED],
  },
  {
    key: 'action_required',
    operator: ApiQueryOperator.in,
    value: EmployeeActionRequiredType.INVITE_EXPIRED,
  },
];

const inviteFailureStatusFilters = [
  {
    key: 'status',
    operator: ApiQueryOperator.in,
    value: [EmployeeStatus.INVITED, EmployeeStatus.ACTION_REQUIRED],
  },
  {
    key: 'action_required',
    operator: ApiQueryOperator.in,
    value: EmployeeActionRequiredType.INVITE_SEND_FAILURE,
  },
];

const reviewStatusOptions = [
  {
    value: actionRequiredFilters,
    label: 'All',
  },
  {
    value: pendingStatusFilters,
    label: 'Pending',
  },
  {
    value: inviteFailureStatusFilters,
    label: 'Invited - Delivery Failure',
  },
  {
    value: expiredStatusFilters,
    label: 'Expired',
  },
];

const ALL_STATUSES = ['approved', 'invited', 'pending', 'expired', 'dismissed'];
const DEFAULT_STATUS_FILTERS = ['approved', 'invited', 'pending', 'expired'];

const getStatusFiltersLabels = (query: IApiQueryParam[]) => {
  const statusLabels = [];
  const statusFilter = (query || []).find((q: IApiQueryParam) => q.key === 'status' && q.operator === ApiQueryOperator.in)?.value;
  const actionRequiredFilter = (query || []).find((q: IApiQueryParam) => q.key === 'action_required' && q.operator === ApiQueryOperator.in)?.value;

  // get status labels from query
  if (statusFilter?.includes(EmployeeStatus.APPROVED)) statusLabels.push(EmployeeStatus.APPROVED);
  if (statusFilter?.includes(EmployeeStatus.DISMISSED)) statusLabels.push(EmployeeStatus.DISMISSED);
  if (statusFilter?.includes(EmployeeStatus.INVITED)) statusLabels.push(EmployeeStatus.INVITED);
  if (statusFilter?.includes(EmployeeStatus.ACTION_REQUIRED) && actionRequiredFilter?.includes(EmployeeActionRequiredType.EMPLOYER_APPROVAL)) statusLabels.push('pending');
  if (statusFilter?.includes(EmployeeStatus.ACTION_REQUIRED) && actionRequiredFilter?.includes(EmployeeActionRequiredType.INVITE_EXPIRED)) statusLabels.push('expired');

  return statusLabels;
};

const getFilters = (newStatus: string[], query: IApiQueryParam[] = []) => {
  const otherFilters = Object.values(query || []).filter((q) => q.key !== 'status' && q.key !== 'action_required');

  const statusFilters: string[] = newStatus.map((s) => (s === 'expired' || s === 'pending' ? EmployeeStatus.ACTION_REQUIRED : s));
  const actionRequired: string[] = [];

  if (newStatus.includes('expired')) {
    actionRequired.push(EmployeeActionRequiredType.INVITE_EXPIRED);
  }
  if (newStatus.includes('pending')) {
    actionRequired.push(EmployeeActionRequiredType.EMPLOYER_APPROVAL);
  }
  if (newStatus.includes('invited')) {
    if (!statusFilters.includes(EmployeeStatus.ACTION_REQUIRED)) {
      statusFilters.push(EmployeeStatus.ACTION_REQUIRED);
    }
    actionRequired.push(EmployeeActionRequiredType.INVITE_SEND_FAILURE);
  }
  if (newStatus.some((s) => s === EmployeeStatus.INVITED || s === EmployeeStatus.APPROVED || s === EmployeeStatus.DISMISSED)) {
    actionRequired.push(EmployeeActionRequiredType.NONE);
  }

  const filters = [...otherFilters];

  if (statusFilters.length === 0) {
    // Handle case where no status have been selected by the user (no checkboxes selected on all users tab)
    filters.push({
      key: 'status',
      operator: ApiQueryOperator.nin,
      value: [EmployeeStatus.APPROVED, EmployeeStatus.INVITED, EmployeeStatus.ACTION_REQUIRED, EmployeeStatus.DISMISSED],
    });
  } else {
    filters.push({
      key: 'status',
      operator: ApiQueryOperator.in,
      value: statusFilters,
    });
  }

  if (actionRequired.length > 0) {
    filters.push({
      key: 'action_required',
      operator: ApiQueryOperator.in,
      value: actionRequired,
    });
  }

  return filters;
};

const allUsersFilters = getFilters(DEFAULT_STATUS_FILTERS);

const getDisplayFilters = (q: IApiQueryParam[] = []) => q.filter((item) => ['name', 'type', 'sites.job.title'].includes(item.key) || (item.key === 'action_required' && actionRequired.includes(item.value as EmployeeActionRequiredType)));

export const Users = ({ activeTab }: { activeTab: EmployeesTab }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [filterActive, setFilterActive] = useState<boolean>(false);
  const [reviewEmployeeModalVisible, setReviewEmployeeModalVisible] = useState<boolean>(false);
  const [resendEmployeeModalVisible, setResendEmployeeModalVisible] = useState<boolean>(false);
  const [currentResendEmployee, setCurrentResendEmployee] = useState<UserTableDataProps | undefined>(undefined);
  const [statusFilters] = useState(DEFAULT_STATUS_FILTERS);

  const currentEmployee = useSelector(selectCurrentEmployee());
  const currentOrganizationId = currentEmployee?.organizationId;
  const currentSiteId = currentEmployee?.siteId;

  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Employees.GET_EMPLOYEES_REQUEST));
  const isResendLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Employee.UPDATE_EMPLOYEE_REQUEST));

  const currentOrganization = useSelector(selectCurrentEmployeeOrg);
  const currentSite = useSelector(selectCurrentEmployeeSite);
  const currentSiteAcceptance = useSelector(selectSiteAcceptance());

  const { data: employees, total: totalEmployees } = useSelector(selectEmployees()) || { data: [], total: null };
  const requiresReviewCount = useSelector(selectReviewRequiredEmployeesTotal()) || 0;
  const totalSelector = () => totalEmployees;

  const affiliationType = currentSiteId ? AffiliationType.SITES : AffiliationType.ORGANIZATIONS;
  const affiliationId = currentSiteId || currentOrganizationId;

  const fetchEmployees = (query: FetchDataQueryType) => {
    if (currentOrganizationId) {
      const options: IApiQueryOptions = {
        ...query,
        includeDismissed: true,
      };
      dispatch(getEmployeesRequest(affiliationType, affiliationId, options));
    }
  };

  useEffect(() => {
    if (affiliationId) {
      dispatch(getActionRequiredEmployeesCountRequest(affiliationType, affiliationId));
    }
  }, [dispatch, affiliationId, affiliationType]);

  useEffect(() => {
    if (activeTab === EmployeesTab.ACCEPTANCE && (!currentSiteId || (currentSiteAcceptance && !currentSiteAcceptance.enabled))) {
      history.push(routeConstants.private.employees);
    }
  }, [history, activeTab, currentSiteId, currentSiteAcceptance]);

  const disableReviewButton = !useSelector(selectIsPermitted([EmployeeRole.SITE_ADMIN, EmployeeRole.ADMIN]));

  const listContentComponent = ({ activeTab, statusLabels, onSortChange, onStatusChange, isSortedBy, isSortDesc, pagination, resultsPerPage, limit, q }: IListContentComponentType) => (
    <Section data-cy="table">
      <SectionHeader
        title="Users"
        value={`(${statusFilters.length !== 0 ? totalEmployees || 0 : 0})`}
        isLoading={isLoading}
        filterActive={filterActive}
        staticSubtitle
        subtitle={onStatusChange && <StatusCheckbox label="User Status:" statuses={ALL_STATUSES} activeStatus={statusLabels} onChange={(s: string[]) => onStatusChange(s)} />}
      >
        <FilterButton filterActive={filterActive} appliedFilters={getDisplayFilters(q).length} onClick={() => setFilterActive(!filterActive)} />
      </SectionHeader>
      <UsersFilters isVisible={filterActive} data-cy="filter-section" roleVisible statusVisible={activeTab === EmployeesTab.REVIEW} statusOptions={reviewStatusOptions} jobTitleVisible site={currentSite} organization={currentOrganization} />
      <UserTable
        disableReviewButton={disableReviewButton}
        onSortChange={onSortChange}
        isSortedBy={isSortedBy}
        isSortDesc={isSortDesc}
        data={
          employees && statusFilters.length !== 0
            ? employees.map((employee: IEmployee) => ({
                id: employee.id,
                username: employee.name,
                avatarUrl: employee.user?.avatarUrl,
                type: employee.type as EmployeeType,
                status: currentSiteId ? employee.sites.find((item) => item.id === currentSiteId)?.status || employee.status : employee.status,
                actionRequired: employee.actionRequired,
                inviteRecipient: employee.inviteRecipient || '',
                sites: employee.sites,
                createdAt: employee.createdAt,
                hasPaymentMethod: !!employee.user?.card,
              }))
            : []
        }
        isLoading={isLoading}
        tableHeaders={tableHeaders[activeTab]}
        onEmployeeSelect={(employee) => {
          history.push({
            pathname: `/users/${employee.id}`,
          });
        }}
        onReviewSelect={(id: string) => {
          setReviewEmployeeModalVisible(true);
          dispatch(getEmployeeRequest(id));
        }}
        onResendSelect={(id: string, employee: UserTableDataProps) => {
          setCurrentResendEmployee(employee);
          setResendEmployeeModalVisible(true);
        }}
        pagination={pagination}
        resultsPerPage={resultsPerPage}
        limit={limit}
      />
    </Section>
  );

  return (
    <>
      <PageHeader>
        <PageLabel>Users</PageLabel>
        <PageHeaderContent>
          <ConnectedSiteSelector allSitesEnabled appearance={SiteSelectorAppearance.PAGE_HEADER} />
          <PageActions>
            <PermissionGate requiredRoles={[EmployeeRole.ADMIN, EmployeeRole.SITE_ADMIN]}>
              <PageHeaderAction appearance="tertiary" label="Add User" to={routeConstants.private.addEmployee} />
            </PermissionGate>
          </PageActions>
        </PageHeaderContent>
        <TabNav>
          <TabNavItem label="All Users" data-cy="tab-all" to={routeConstants.private.employees} isActive={activeTab === EmployeesTab.ALL} exact />
          <TabNavItem label="Needs Review" data-cy="tab-review" to={routeConstants.private.employeesNeedingApproval} isActive={activeTab === EmployeesTab.REVIEW} isUpdated={requiresReviewCount > 0} />
          {currentSiteAcceptance?.enabled && <TabNavItem label="Tip Acceptance" data-cy="tab-acceptance" to={routeConstants.private.employeesAcceptance} isActive={activeTab === EmployeesTab.ACCEPTANCE} />}
        </TabNav>
      </PageHeader>

      {activeTab === EmployeesTab.ALL && (
        <ListContextProvider fetchData={fetchEmployees} totalSelector={totalSelector} defaultSort={DEFAULT_SORT} persistURL={false} dataKey="users" dependencies={{ currentSiteId }} defaultFilters={allUsersFilters}>
          <ListContext.Consumer>
            {({ methods, query, components }) => {
              const { onSortChange, isSortedBy, isSortDesc } = methods;
              const { pagination, resultsPerPage } = components;
              const { limit, q } = query;

              const statusLabels = getStatusFiltersLabels(q);
              const onStatusChange = (newStatus: string[]) => {
                methods.onFiltersChange(getFilters(newStatus, q));
              };

              return listContentComponent({
                activeTab,
                statusLabels,
                onSortChange,
                onStatusChange,
                isSortedBy,
                isSortDesc,
                pagination,
                resultsPerPage,
                limit,
                q,
              });
            }}
          </ListContext.Consumer>
        </ListContextProvider>
      )}

      {activeTab === EmployeesTab.REVIEW && (
        <ListContextProvider fetchData={fetchEmployees} totalSelector={totalSelector} defaultSort={DEFAULT_SORT} persistURL dataKey="users" dependencies={{ currentSiteId }} defaultFilters={actionRequiredFilters}>
          <ListContext.Consumer>
            {({ methods, query, components }) => {
              const { fetchData, onSortChange, isSortedBy, isSortDesc } = methods;
              const { pagination, resultsPerPage } = components;
              const { limit, q } = query;

              return (
                <>
                  <ReviewUserScreen
                    onClose={() => {
                      setReviewEmployeeModalVisible(false);
                    }}
                    onDeny={() => {
                      setReviewEmployeeModalVisible(false);
                    }}
                    onApprove={() => {
                      fetchData(query);
                    }}
                    visible={reviewEmployeeModalVisible}
                  />
                  <ModalFactory customModal isActive={resendEmployeeModalVisible} onClose={() => setResendEmployeeModalVisible(false)}>
                    <ConfirmResendInvite
                      onClose={() => setResendEmployeeModalVisible(false)}
                      onResend={() => {
                        if (currentResendEmployee && currentResendEmployee.inviteRecipient) {
                          dispatch(
                            resendInvitationRequest(currentResendEmployee?.id, currentResendEmployee.inviteRecipient, {
                              onSuccess: () => {
                                setResendEmployeeModalVisible(false);
                                fetchData(query);
                              },
                            })
                          );
                        }
                      }}
                      isLoading={isResendLoading}
                      employee={currentResendEmployee}
                    />
                  </ModalFactory>

                  {listContentComponent({
                    activeTab,
                    statusLabels: [],
                    onSortChange,
                    isSortedBy,
                    isSortDesc,
                    pagination,
                    resultsPerPage,
                    limit,
                    q,
                  })}
                </>
              );
            }}
          </ListContext.Consumer>
        </ListContextProvider>
      )}

      {activeTab === EmployeesTab.ACCEPTANCE && <TipAcceptanceUsers />}
    </>
  );
};
